import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { formatPrice } from 'common/formatters';
import emptyFunction from 'common/util/emptyFunction';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_PARTNER_SUMMARY_VIEWED,
  ANALYTICS_EVENT_REVENUE_EXPLAINER_VIEWED,
} from 'client/analytics/eventList';
import safeLocalStorage from 'client/lib/safeLocalStorage';
import UPPartnerPayoutFlow from 'client/components/UPPartnerPayoutFlow/UPPartnerPayoutFlow';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrText from 'client/components/SrText';
import SrTooltip, {
  SR_TOOLTIP_ICON_QUESTION_MARK_BLUE,
  SR_TOOLTIP_POSITION_CENTER_UNDERNEATH,
} from 'client/components/SrTooltip';

import './UPActivationsSummary.less';

const localStorageKeyForExplainerFirstView = (partnerId) =>
  `revenueExplainerSeen:${partnerId}`;

const UPActivationsSummary = ({
  isPartnerAccounting,
  isLegacyTransferBlocked = false,
  utilityPartner,
  expectedRevenue,
  collectedRevenue,
  transferOutRevenue = 0,
  utilityPayouts = [],
  utilityPartnerStripeAccounts = [],
}) => {
  const [payoutFlowArgs, setPayoutFlowArgs] = useState(null);

  const utilityPartnerStripeAccount = utilityPartnerStripeAccounts.find((account) => {
    return utilityPartner.utilityStripeAccountId === account.id;
  });

  const summaryViewedFiredRef = useRef(false);
  const explainerViewedFiredRef = useRef(false);
  const partnerId = utilityPartner?.id;

  useEffect(() => {
    if (!partnerId || summaryViewedFiredRef.current) {
      return;
    }
    summaryViewedFiredRef.current = true;
    analytics.track(ANALYTICS_EVENT_PARTNER_SUMMARY_VIEWED, {
      partnerId,
      upcomingRevenueAmount: expectedRevenue || 0,
      transferOutRevenueAmount: transferOutRevenue,
      totalEarned: (expectedRevenue || 0) + transferOutRevenue,
    });
  }, [partnerId, expectedRevenue, transferOutRevenue]);

  const handleExplainerHover = () => {
    if (!partnerId || explainerViewedFiredRef.current) {
      return;
    }
    explainerViewedFiredRef.current = true;
    // If storage is unavailable, getItem returns null → firstView=true (intended analytics fallback).
    const explainerSeenKey = localStorageKeyForExplainerFirstView(partnerId);
    const firstView = !safeLocalStorage.getItem(explainerSeenKey);
    analytics.track(ANALYTICS_EVENT_REVENUE_EXPLAINER_VIEWED, {
      partnerId,
      firstView,
    });
    if (firstView) {
      safeLocalStorage.setItem(explainerSeenKey, '1');
    }
  };

  const cards = [
    {
      title: 'Future Revenue',
      subtitle: `${formatPrice(expectedRevenue)} upcoming`,
      linkText: isLegacyTransferBlocked ? null : `Transfer ${formatPrice(transferOutRevenue)} now`,
      styleName: classnames('currentBalance', { pending: transferOutRevenue > 0 }),
      tooltipText:
        'Utility companies pay referral commissions slowly. ' +
        'Transfer to your bank once they pay.',
      onTooltipHover: handleExplainerHover,
      onClick: isPartnerAccounting
        ? () => {
            setPayoutFlowArgs({
              utilityPayouts,
              utilityPartner,
              utilityPartnerStripeAccount,
              onClose: () => setPayoutFlowArgs(null),
            });
          }
        : null,
    },
    {
      title: 'Collected Revenue',
      subtitle: `${formatPrice(collectedRevenue)} total`,
      styleName: 'collectedRevenue',
      tooltipText: "The total amount of money you've already transferred to your bank.",
    },
  ];

  return (
    <div styleName="container">
      <div styleName="titleBox">
        <span styleName="titleText">Summary</span>
        <SrBox marginLeft="15px" display="inlineBlock">
          <SrLink to="/payout-how-it-works" target="_blank">
            <SrText font="greycliff" weight="700" fontSize="14px" color="blue300">
              Learn how payouts work →
            </SrText>
          </SrLink>
        </SrBox>
      </div>
      <div styleName="cardsBox">
        {cards.map((card) => {
          return (
            <div key={card.title} styleName={classnames('cardBox', card.styleName)}>
              <div styleName="tooltip" onMouseEnter={card.onTooltipHover}>
                <SrTooltip
                  bgColor="blueBright"
                  className="activation-summary"
                  iconName={SR_TOOLTIP_ICON_QUESTION_MARK_BLUE}
                  position={SR_TOOLTIP_POSITION_CENTER_UNDERNEATH}
                >
                  {card.tooltipText}
                </SrTooltip>
              </div>
              <div styleName="iconBox">
                <div styleName="icon" />
              </div>
              <div styleName="textBox">
                <span styleName="title">{card.title}</span>
                <span styleName="subtitle">{card.subtitle}</span>
                {card.linkText && (
                  <span styleName="link" onClick={card.onClick || emptyFunction}>
                    {card.linkText}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {payoutFlowArgs && <UPPartnerPayoutFlow {...payoutFlowArgs} />}
    </div>
  );
};

UPActivationsSummary.propTypes = {
  isLegacyTransferBlocked: PropTypes.bool,
  utilityPayouts: PropTypes.array.isRequired,
  collectedRevenue: PropTypes.number,
  expectedRevenue: PropTypes.number,
  transferOutRevenue: PropTypes.number,
  utilityPartner: PropTypes.object,
  utilityPartnerStripeAccounts: PropTypes.array,
  unlistedAddressesCount: PropTypes.number,
  onFocusUnlistedAddresses: PropTypes.func,
};

UPActivationsSummary.displayName = 'UPActivationsSummary';

export default UPActivationsSummary;
